import {
    faClock,
    faExternalLinkAlt,
    faFile,
    faPlayCircle,
    faStepForward,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Spinner } from '@ui';
import { cx } from 'class-variance-authority';
import { Event, EventPriceClass } from 'components/EventCalendar/types';
import { refTable } from 'components/TrainingPlan/TrainingPlanHelpers';
import { UserAvatar } from 'components/User';
import { WYSIWYGText } from 'components/WYSIWYGText';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useEventPrice } from '../hooks/useEventPrice';

interface Props {
    event: Event;
    email?: string;
    pricingTableTotal?: number;
    /** The club member the booking is for — the summary has to quote that member's price. */
    memberId?: number | string | null;
}

const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

export const EventSummary = ({ event, email: _email, pricingTableTotal, memberId }: Props) => {
    const { t } = useTranslation('bookings');
    const email = useDebouncedValue(_email, 500);
    const { data: pricesData, error: pricesError } = useEventPrice(event, email, memberId);
    const isLoading = !pricesData && !pricesError;

    const start = moment(event?.start);
    const repeat_until = moment(event?.repeat_until);
    const deadline = moment(event?.deadline);

    const translatePriceCategory = (category: EventPriceClass['category']) => {
        switch (category) {
            case 'default':
                return t('event.summary.priceCategory.default');

            case 'member':
                return t('event.summary.priceCategory.member');

            case 'relative':
                return t('event.summary.priceCategory.relative');

            default:
                return t('event.summary.priceCategory.category');
        }
    };

    return (
        <div className="tw-card-bordered tw-flex tw-flex-col tw-gap-4">
            <h3 className="tw-mb-0">{event.title}</h3>
            <div className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2" />
            {event.trainers && event.trainers.filter((user) => user.visible !== false).length > 0 && (
                <>
                    <h5 className="tw-mb-0 tw-text-right tw-font-semibold">{t('event.summary.supervisedBy')}</h5>
                    <div className="tw-mb-6 tw-grid tw-grid-cols-4 tw-gap-8">
                        {event.trainers
                            .filter((user) => user.visible !== false)
                            .map((user) => (
                            <div className="tw-w-full tw-text-center" key={user.id}>
                                <UserAvatar user={user} />
                                <h5 className="tw-mt-4">{user.name}</h5>
                            </div>
                        ))}
                    </div>
                </>
            )}
            <h5 className="tw-mb-0 tw-text-right tw-font-semibold">{t('event.summary.general')}</h5>
            <div className="tw-grid tw-grid-cols-[auto_1fr] tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm tw-text-accents-7">
                {event.start && (
                    <>
                        <FontAwesomeIcon size="1x" icon={faPlayCircle} />
                        <span>{`${start.format('ddd')} ${start.format('l')}${
                            event.repeat_until
                                ? ` ${t('common.until')} ${repeat_until.format('ddd')} ${repeat_until.format('l')}`
                                : ''
                        }`}</span>
                    </>
                )}

                {event.start_time && (
                    <>
                        <FontAwesomeIcon size="1x" icon={faClock} />
                        <span>{`${refTable.times[event.start_time]} ${t('common.until')} ${
                            refTable.times[event.end_time]
                        }`}</span>
                    </>
                )}

                {event.deadline && (
                    <>
                        <FontAwesomeIcon size="1x" icon={faStepForward} />
                        <span>{`${deadline.format('ddd')} ${deadline.format('l LT')}`}</span>
                    </>
                )}

                {typeof event.max_participants === 'number' && (
                    <>
                        <FontAwesomeIcon size="1x" icon={faUsers} />
                        <span>{`${event.bookings_count} / ${event.max_participants}`}</span>
                    </>
                )}
            </div>
            {event.prices?.length > 0 && (
                <>
                    <h5 className="tw-mb-0 tw-text-right tw-font-semibold">
                        {pricesData?.data?.length > 1 ? t('event.summary.priceTiers') : t('event.summary.price')}
                    </h5>
                    {isLoading ? (
                        <span className="tw-flex tw-justify-center">
                            <Spinner className="tw-my-6" color="success" />
                        </span>
                    ) : (
                        <>
                            {!pricesData?.success && <Blockquote>{t('event.summary.emailPrompt')}</Blockquote>}
                            {pricesData?.data
                                ?.filter((price) => !price.hidden || price.active)
                                .map((price, index) => {
                                    const active = price.active && pricesData?.success;
                                    const disabled = !active && pricesData?.success;
                                    return (
                                        <div
                                            className={cx(
                                                active && 'tw-selected',
                                                disabled && 'tw-line-through',
                                                'tw-card-flat tw-selectable tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-2 tw-px-4'
                                            )}
                                            key={`${price.category}_${index}`}
                                        >
                                            <span className="tw-text-lg">{translatePriceCategory(price.category)}</span>
                                            {!!price.units && (
                                                <span className="tw-font-bold">
                                                    {t('event.summary.units', { count: price.units })}
                                                </span>
                                            )}
                                            <span className="tw-font-bold">{price.price} €</span>
                                        </div>
                                    );
                                })}
                        </>
                    )}
                </>
            )}
            {pricingTableTotal != null && pricingTableTotal > 0 && (
                <div
                    className="tw-card-flat tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-2 tw-px-4 tw-bg-success/5 tw-border tw-border-solid tw-border-success/30 tw-rounded-lg"
                >
                    <span className="tw-text-sm tw-text-accents-7">{t('event.summary.bookedPositions')}</span>
                    <span className="tw-font-bold tw-text-success">{formatPrice(pricingTableTotal)}</span>
                </div>
            )}
            {event.description && (
                <>
                    <h5 className="tw-mb-0 tw-text-right tw-font-semibold">{t('common.description')}</h5>
                    <WYSIWYGText>{event.description}</WYSIWYGText>
                </>
            )}
            {event.files?.length > 0 && (
                <>
                    <h5 className="tw-mb-0 tw-text-right tw-font-semibold">{t('event.summary.files')}</h5>
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        {event.files.map((file) => (
                            <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-unstyled"
                                key={file.id}
                            >
                                <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-md tw-border tw-border-accents-6 tw-bg-white tw-p-4 tw-shadow">
                                    <FontAwesomeIcon icon={faFile} />
                                    <span className="tw-flex-1 tw-text-lg">{file.name}</span>
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </div>
                            </a>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default EventSummary;
